import React, { useState } from 'react';
import { Upload, Check, AlertCircle } from 'lucide-react';
type DataType = 'nmls' | 'rateSheet' | 'stateLicenses';
interface DataCollectionFormProps {
  type: DataType;
  detectedValue?: string;
  onSubmit: (value: string) => void;
  onSkip?: () => void;
}
export function DataCollectionForm({
  type,
  detectedValue,
  onSubmit,
  onSkip
}: DataCollectionFormProps) {
  const [value, setValue] = useState(detectedValue || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 800));
    onSubmit(value);
    setIsSubmitting(false);
  };
  const config = {
    nmls: {
      title: 'NMLS ID',
      placeholder: 'Enter your NMLS ID (e.g., 123456)',
      description: 'Your National Mortgage Licensing System identifier',
      inputType: 'text'
    },
    rateSheet: {
      title: 'Current Rate Sheet',
      placeholder: 'Upload rate sheet or enter APR',
      description: 'Upload your current rate sheet or manually enter rates',
      inputType: 'text'
    },
    stateLicenses: {
      title: 'State Licenses',
      placeholder: 'Enter licensed states (e.g., CA, TX, NY)',
      description: 'States where you are licensed to operate',
      inputType: 'text'
    }
  };
  const formConfig = config[type];
  return <div className="bg-slate-50 border border-slate-200 rounded p-4 mt-3" data-id="element-178">
      <div className="flex items-start gap-3 mb-3" data-id="element-179">
        <div className="bg-slate-200 p-1.5 rounded" data-id="element-180">
          <AlertCircle className="w-4 h-4 text-slate-600" data-id="element-181" />
        </div>
        <div className="flex-1" data-id="element-182">
          <h5 className="text-sm font-semibold text-slate-900 mb-1" data-id="element-183">
            Setup Required: {formConfig.title}
          </h5>
          <p className="text-xs text-slate-600" data-id="element-184">{formConfig.description}</p>
        </div>
      </div>

      {detectedValue && <div className="bg-white border border-slate-300 rounded p-3 mb-3" data-id="element-185">
          <div className="flex items-center justify-between" data-id="element-186">
            <div data-id="element-187">
              <p className="text-xs text-slate-600 mb-1" data-id="element-188">
                We detected this value:
              </p>
              <p className="text-sm font-mono font-semibold text-slate-900" data-id="element-189">
                {detectedValue}
              </p>
            </div>
            <button onClick={() => onSubmit(detectedValue)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded transition-colors" data-id="element-190">
              <Check className="w-3.5 h-3.5" data-id="element-191" />
              Confirm
            </button>
          </div>
        </div>}

      <form onSubmit={handleSubmit} className="space-y-3" data-id="element-192">
        <div data-id="element-193">
          <input type={formConfig.inputType} value={value} onChange={e => setValue(e.target.value)} placeholder={formConfig.placeholder} className="w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent" required data-id="element-194" />
        </div>

        {type === 'rateSheet' && <div className="flex items-center gap-2" data-id="element-195">
            <button type="button" className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-slate-600 bg-white border border-slate-300 hover:bg-slate-50 rounded transition-colors" data-id="element-196">
              <Upload className="w-3.5 h-3.5" data-id="element-197" />
              Upload File
            </button>
            <span className="text-xs text-slate-500" data-id="element-198">or enter manually</span>
          </div>}

        <div className="flex items-center gap-2" data-id="element-199">
          <button type="submit" disabled={isSubmitting || !value} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed rounded transition-colors" data-id="element-200">
            {isSubmitting ? <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-201" />
                Verifying...
              </> : <>
                <Check className="w-4 h-4" data-id="element-202" />
                Save & Verify
              </>}
          </button>
          {onSkip && <button type="button" onClick={onSkip} className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors" data-id="element-203">
              Skip for now
            </button>}
        </div>
      </form>

      <p className="text-xs text-slate-600 mt-3" data-id="element-204">
        💡 This data will be saved to your company profile for future reviews.
      </p>
    </div>;
}